import React, { useRef, useEffect } from 'react';
import { X, Copy, MapPin, Lightbulb, AlertCircle, CheckCircle, Shield } from 'lucide-react';

export const HELP_CONTENT = {
    // ── PUBLIC INTERNET FIELDS ──
    host_public: {
        title: "Host / IP Address",
        aws: [
            "Sign in to the AWS Management Console.",
            "Go to RDS Dashboard -> Databases.",
            "Select your database instance.",
            "Under the 'Connectivity & security' tab, copy the Endpoint."
        ],
        gcp: [
            "Sign in to the Google Cloud Console.",
            "Go to SQL Dashboard.",
            "Select your database instance.",
            "Copy the 'Public IP address' from the Overview tab."
        ],
        azure: [
            "Sign in to the Azure Portal.",
            "Go to Azure Database.",
            "Select your server instance.",
            "Copy the 'Server name' from the Overview section."
        ]
    },
    port_public: {
        title: "Port",
        aws: [
            "Sign in to the AWS Management Console.",
            "Go to RDS Dashboard -> Databases.",
            "Select your database instance.",
            "Under the 'Connectivity & security' tab, find the Port number."
        ],
        gcp: [
            "Sign in to the Google Cloud Console.",
            "Go to SQL Dashboard.",
            "Select your database instance.",
            "Find the port under the Connections tab (default is 5432 or 3306)."
        ],
        azure: [
            "Sign in to the Azure Portal.",
            "Go to Azure Database.",
            "Select your server instance.",
            "Find the port in the Connection strings section."
        ]
    },
    db_name_public: {
        title: "Database Name",
        aws: [
            "Sign in to the AWS Management Console.",
            "Go to RDS Dashboard -> Databases.",
            "Select your database instance.",
            "Under the 'Configuration' tab, copy the DB Name."
        ],
        gcp: [
            "Sign in to the Google Cloud Console.",
            "Go to SQL Dashboard.",
            "Select your database instance.",
            "Check the 'Databases' menu on the left."
        ],
        azure: [
            "Sign in to the Azure Portal.",
            "Go to Azure Database.",
            "Select your server instance.",
            "Check the 'Databases' menu on the left."
        ]
    },
    username_public: {
        title: "Username",
        aws: [
            "Sign in to the AWS Management Console.",
            "Go to RDS Dashboard -> Databases.",
            "Select your database instance.",
            "Under the 'Configuration' tab, find the Master username."
        ],
        gcp: [
            "Sign in to the Google Cloud Console.",
            "Go to SQL Dashboard.",
            "Select your database instance.",
            "Check the 'Users' menu on the left."
        ],
        azure: [
            "Sign in to the Azure Portal.",
            "Go to Azure Database.",
            "Select your server instance.",
            "Find the 'Server admin login name' in the Overview."
        ]
    },

    // ── PRIVATE (SSH TUNNEL) FIELDS ──
    host_private: {
        title: "Host / IP Address (Private)",
        aws: [
            "Sign in to the AWS Management Console.",
            "Go to RDS Dashboard -> Databases.",
            "Select your database instance.",
            "Under 'Connectivity & security', copy the Endpoint."
        ],
        gcp: [
            "Sign in to the Google Cloud Console.",
            "Go to SQL Dashboard.",
            "Select your database instance.",
            "Copy the 'Private IP address' from the Overview tab."
        ],
        azure: [
            "Sign in to the Azure Portal.",
            "Go to Azure Database.",
            "Select your server instance.",
            "Copy the private endpoint address from the Networking tab."
        ]
    },
    port_private: {
        title: "Port (Private)",
        aws: [
            "Sign in to the AWS Management Console.",
            "Go to RDS Dashboard -> Databases.",
            "Select your database instance.",
            "Under 'Connectivity & security', find the Port."
        ],
        gcp: [
            "Default is usually 5432 for Postgres or 3306 for MySQL."
        ],
        azure: [
            "Find the port in the Azure Database Connection strings."
        ]
    },
    db_name_private: {
        title: "Database Name (Private)",
        aws: [
            "Sign in to the AWS Management Console.",
            "Go to RDS Dashboard -> Databases.",
            "Select your database instance.",
            "Under the 'Configuration' tab, copy the DB Name."
        ],
        gcp: [
            "Check the 'Databases' menu in your Google Cloud SQL instance."
        ],
        azure: [
            "Check the 'Databases' menu in your Azure Database instance."
        ]
    },
    username_private: {
        title: "Username (Private)",
        aws: [
            "Sign in to the AWS Management Console.",
            "Go to RDS Dashboard -> Databases.",
            "Select your database instance.",
            "Under the 'Configuration' tab, find the Master username."
        ],
        gcp: [
            "Check the 'Users' menu in your Google Cloud SQL instance."
        ],
        azure: [
            "Find the 'Server admin login' in your Azure Database Overview."
        ]
    },

    // ── SSH SPECIFIC FIELDS ──
    bastion_host: {
        title: "Bastion Host",
        aws: [
            "Sign in to the AWS Management Console.",
            "Go to EC2 Dashboard -> Instances.",
            "Select your Bastion EC2 instance.",
            "Copy the 'Public IPv4 address'."
        ],
        gcp: [
            "Sign in to the Google Cloud Console.",
            "Go to Compute Engine -> VM instances.",
            "Copy the 'External IP' of your bastion VM."
        ],
        azure: [
            "Sign in to the Azure Portal.",
            "Go to Virtual Machines.",
            "Select your bastion VM and copy the 'Public IP address'."
        ]
    },
    ssh_username: {
        title: "SSH Username",
        aws: [
            "For Amazon Linux, the user is typically 'ec2-user'.",
            "For Ubuntu, the user is typically 'ubuntu'.",
            "For Debian, the user is typically 'admin'."
        ],
        gcp: [
            "The username is usually your Google account username.",
            "Alternatively, it might be 'ubuntu' if using an Ubuntu image."
        ],
        azure: [
            "Use the admin username you specified when provisioning the Azure VM."
        ]
    },
    pem_file: {
        title: "Private Key (PEM)",
        aws: [
            "Sign in to the AWS Management Console.",
            "Go to EC2 Dashboard -> Key Pairs.",
            "Locate the .pem file you downloaded when creating the Key Pair."
        ],
        gcp: [
            "Locate the private key generated locally using ssh-keygen.",
            "This key corresponds to the public key added to GCP metadata."
        ],
        azure: [
            "Locate the private key downloaded during VM creation.",
            "Alternatively, retrieve it from your Azure Key Vault."
        ]
    }
};

export function FieldHelpPopover({ fieldId, cloudProvider, onClose }) {
    const popoverRef = useRef(null);
    const content = HELP_CONTENT[fieldId];

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (popoverRef.current && !popoverRef.current.contains(event.target)) {
                onClose();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [onClose]);

    if (!content) return null;

    let platform = 'aws';
    if (cloudProvider === 'gcp') platform = 'gcp';
    else if (cloudProvider === 'azure') platform = 'azure';

    const steps = content[platform] || content.aws;
    const providerName = platform.toUpperCase();

    return (
        <div className="help-popover-card" ref={popoverRef}>
            <div className="help-popover-arrow"></div>
            <div className="help-popover-content">
                <h3>{providerName} {content.title}</h3>
                <ol className="help-popover-steps">
                    {steps.map((step, index) => (
                        <li key={index}>{step}</li>
                    ))}
                </ol>
            </div>
        </div>
    );
}
